import React, { useRef, useState } from 'react'
import hipersendService from '../../services/hipersend'
import CountryCodesModal from '../CountryCodesModal'
import MensagemDeTexto from './MensagemDeTexto'
import {
  BRASIL, CampoDeNumero, TEXTO_RETIDA_POR_SALDO, exibidoDe, mensagemDoErro, numeroCompleto, resultadoDoEnvio, telefoneDe
} from './envioDeTeste'
import { avisoDaSuspensao, linkDeRecargaSeguro } from '../../libs/suspensaoOficial'

// Texto livre na API Oficial, SÓ para teste: até 5 números, sem anexo e sem
// custo. Não passa pelo disparo em massa (que na Oficial continua só com
// modelo) — cada número é um envio avulso, um de cada vez, pelo mesmo caminho
// do "Teste antes no seu número". As marcações do sistema (link de pagamento
// etc.) vão como foram escritas e o servidor resolve no envio.
//
// A Meta só aceita texto livre para quem falou com a conexão nas últimas 24h.
// Isso só se sabe depois que a Meta responde, então o "aceito" aqui é "entrou
// na fila"; a recusa (131047) aparece no Relatório, já traduzida.

export const LIMITE_DE_NUMEROS = 5

let proximoId = 0
const novaLinha = (pais = BRASIL) => ({ id: ++proximoId, pais, digitado: '' })

const TEXTO_DO_ESTADO = {
  enviando: 'Enviando…',
  ok: 'Aceito e na fila. Se o número não falou com você nas últimas 24h, a Meta recusa e o motivo aparece no Relatório.'
}

export default function TextoLivreDeTeste({ api, secret, onVoltar, suspensao, linkRecarga }) {
  const [texto, setTexto] = useState('')
  const [linhas, setLinhas] = useState(() => [novaLinha()])
  const [escolhendoPaisDe, setEscolhendoPaisDe] = useState(null)
  const [etapa, setEtapa] = useState('editar') // editar | confirmar | enviando | feito
  const [resultados, setResultados] = useState([])
  const enviandoAgora = useRef(false)

  // Repetido conta uma vez só: mandar duas vezes o mesmo teste não ajuda ninguém.
  const destinos = []
  linhas.forEach((l) => {
    if (!numeroCompleto(l.digitado)) return
    const telefone = telefoneDe(l.pais, l.digitado)
    if (!destinos.some((d) => d.telefone === telefone)) destinos.push({ telefone, exibido: exibidoDe(l.pais, l.digitado) })
  })
  const incompletos = linhas.filter((l) => l.digitado.trim() && !numeroCompleto(l.digitado)).length
  const podeEnviar = !!texto.trim() && destinos.length > 0 && incompletos === 0
  const travado = etapa === 'confirmar' || etapa === 'enviando'

  const mudarLinha = (id, mudanca) => {
    setLinhas((atual) => atual.map((l) => (l.id === id ? { ...l, ...mudanca } : l)))
    if (etapa === 'feito') setEtapa('editar')
  }

  const enviar = async () => {
    if (enviandoAgora.current) return
    enviandoAgora.current = true
    const fila = destinos
    setEtapa('enviando')
    setResultados(fila.map((d) => ({ ...d, estado: 'enviando' })))
    // Um de cada vez: o resultado de cada número aparece assim que ele volta,
    // e o ritmo fica o mesmo de qualquer envio avulso da conexão.
    for (let i = 0; i < fila.length; i += 1) {
      let r
      try {
        const resposta = await hipersendService.notificacoesInteligentes.sendMessage(api, secret, {
          message: texto,
          phone: fila[i].telefone,
          fileUrl: '' // só texto: o teste livre não leva anexo
        })
        r = resultadoDoEnvio(resposta)
      } catch (e) {
        r = { estado: 'erro', texto: mensagemDoErro(e) }
      }
      setResultados((atual) => atual.map((item, j) => (j === i ? { ...item, ...r } : item)))
    }
    enviandoAgora.current = false
    setEtapa('feito')
  }

  const aviso = avisoDaSuspensao(suspensao, Date.now())
  const recarga = aviso?.ofereceRecarga ? linkDeRecargaSeguro(linkRecarga) : null

  return (
    <div className="dp-livre">
      <CountryCodesModal
        open={escolhendoPaisDe !== null}
        onClose={() => setEscolhendoPaisDe(null)}
        onSelect={(p) => { mudarLinha(escolhendoPaisDe, { pais: p }); setEscolhendoPaisDe(null) }}
      />

      <div className="dp-livre-cab">
        <div>
          <h4>Mensagem livre — só teste</h4>
          <p className="dp-sub">Vai direto para até {LIMITE_DE_NUMEROS} números. Não é disparo para a lista.</p>
        </div>
        <span className="dp-sem-custo">Sem custo</span>
      </div>

      <p className="dp-janela">
        A Meta só entrega texto livre para quem falou com esta conexão nas últimas 24h. Para os demais, use um modelo.
      </p>

      {aviso && (
        <div className="dp-selo bad" role="status">
          <span className="ic">!</span>
          <div>
            <b>{aviso.titulo}</b>
            <span>{aviso.ofereceRecarga ? TEXTO_RETIDA_POR_SALDO : aviso.rodape}</span>
            {recarga && <> {' '}<a href={recarga} target="_blank" rel="noopener noreferrer">Recarregar</a></>}
          </div>
        </div>
      )}

      <fieldset disabled={travado} className="dp-livre-campos">
        <MensagemDeTexto
          texto={texto}
          onTexto={(t) => { setTexto(t); if (etapa === 'feito') setEtapa('editar') }}
          variaveis={[]}
          mostrarArquivo={false}
          rotuloPrevia="Como chega no WhatsApp"
          placeholder="Escreva o texto do teste. Marcações do sistema, como o link de pagamento, são resolvidas no envio."
        />

        <div className="dp-bloco">
          <h4>Para quem</h4>
          <p className="dp-sub">Número com país e DDD. Até {LIMITE_DE_NUMEROS}.</p>
          <div className="dp-livre-numeros">
            {linhas.map((l, i) => (
              <div className="dp-teste" key={l.id}>
                <CampoDeNumero
                  pais={l.pais}
                  onEscolherPais={() => setEscolhendoPaisDe(l.id)}
                  rotulo={`Número ${i + 1} com DDD`}
                  valor={l.digitado}
                  onValor={(v) => mudarLinha(l.id, { digitado: v })}
                />
                {linhas.length > 1 && (
                  <button
                    type="button"
                    className="dp-btn perigo"
                    aria-label={`Remover número ${i + 1}`}
                    onClick={() => setLinhas((atual) => atual.filter((x) => x.id !== l.id))}
                  >
                    Remover
                  </button>
                )}
              </div>
            ))}
          </div>
          {linhas.length < LIMITE_DE_NUMEROS && (
            <button
              type="button"
              className="dp-btn fantasma inicio"
              onClick={() => setLinhas((atual) => [...atual, novaLinha(atual[atual.length - 1]?.pais)])}
            >
              + Adicionar número
            </button>
          )}
          {incompletos > 0 && <p className="dp-erro" style={{ marginTop: 6 }}>Complete o DDD e o número, ou apague o campo.</p>}
        </div>
      </fieldset>

      {travado ? (
        <div className="dp-confirma-teste">
          <span>
            Enviar este texto para <b>{destinos.length} {destinos.length === 1 ? 'número' : 'números'}</b>
            {' '}({destinos.map((d) => d.exibido).join(', ')})? É um teste: sem custo e fora do disparo.
          </span>
          <div className="dp-linha-botoes">
            <button type="button" className="dp-btn" onClick={() => setEtapa('editar')} disabled={etapa === 'enviando'}>
              Voltar
            </button>
            <button type="button" className="dp-btn pri" onClick={enviar} disabled={etapa === 'enviando'}>
              {etapa === 'enviando' ? 'Enviando…' : 'Enviar teste agora'}
            </button>
          </div>
        </div>
      ) : (
        <div className="dp-linha-botoes">
          <button type="button" className="dp-btn pri" disabled={!podeEnviar} onClick={() => setEtapa('confirmar')}>
            {destinos.length > 1 ? `Enviar teste para ${destinos.length} números` : 'Enviar teste'}
          </button>
          <button type="button" className="dp-btn fantasma" onClick={onVoltar}>
            Voltar para o modelo
          </button>
        </div>
      )}

      {resultados.length > 0 && (
        <ul className="dp-resultados" aria-label="Resultado do teste" aria-live="polite">
          {resultados.map((r) => (
            <li key={r.telefone} className={r.estado}>
              <b>{r.exibido}</b>
              <span>{r.texto || TEXTO_DO_ESTADO[r.estado]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
